import React, { ChangeEvent, useEffect, useState } from "react";
import styled from "styled-components";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { FileInput } from "@sonder/ui/source/styled/components/FileInput";
import { Image } from "@sonder/ui/source/styled/components/Image";
import { ImageSlide } from "@sonder/ui/source/styled/components/ImageSlide";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { ImageGallery } from "@sonder/ui/source/styled/fragments/ImageGallery";
import { ImageSlide as ImageSlideFragment } from "@sonder/ui/source/styled/fragments/ImageSlide";
import { colors } from "@sonder/ui/source/styled/theme";

// We are overriding some common styles here. Adding these consts to make it easier to tweak.
const CUSTOM_IMAGE_HEIGHT = "8rem";
const CUSTOM_IMAGE_MARGIN = "0.75rem";
const CUSTON_CANCEL_BUTTON_SIZE = "1.5rem";

const StyledCancelButton = styled(CancelButton)`
  background: ${colors.white};
  border-radius: 50%;
  position: absolute;
  right: 0.25rem;
  top: 0.25rem;

  > svg {
    height: ${CUSTON_CANCEL_BUTTON_SIZE};
    width: ${CUSTON_CANCEL_BUTTON_SIZE};
  }
`;

const StyledFileInput = styled(FileInput)`
  align-items: center;
  border: 1px solid ${colors.blue7};
  border-radius: 0.3rem;
  color: ${colors.blue7};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: ${CUSTOM_IMAGE_MARGIN};
  min-width: ${CUSTOM_IMAGE_HEIGHT};
  min-height: ${CUSTOM_IMAGE_HEIGHT};

  &:hover:not(:disabled) {
    background: ${colors.blueF};
  }

  &:active:not(:disabled) {
    background: ${colors.blue7};
    border: 1px solid ${colors.blue7};
    color: ${colors.white};
  }

  &:disabled {
    color: ${colors.grayA};
    border-color: ${colors.grayF};
  }
`;

const StyledImageGallery = styled(ImageGallery)`
  > ${ImageSlideFragment} {
    &:not(:last-child) {
      margin-right: ${CUSTOM_IMAGE_MARGIN};
    }
  }
`;

interface FileWrapper {
  id: string;
  file: File;
  url: string;
}

const convertToFileWrapper = (file: File) => {
  return {
    id: `${file.name}-${Date.now()}`,
    file,
    url: URL.createObjectURL(file),
  };
};

interface FileInputAttachmentsProps {
  accept?: string;
  files?: File[];
  onChange?: (files: File[]) => void;
}

export const FileInputAttachments = ({
  accept = "image/png,image/jpeg,image/gif,application/pdf",
  files,
  onChange,
}: FileInputAttachmentsProps) => {
  const [fileWrappers, setFileWrappers] = useState<FileWrapper[]>(
    (files || []).map(convertToFileWrapper),
  );
  const onImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFileWrappers(
      fileWrappers.concat(
        Array.from(e.target.files || []).map(convertToFileWrapper),
      ),
    );
    // Clearing the value fixes a bug/feature where you try to upload the same file twice.
    e.target.value = "";
  };
  const onImageDelete = (fileWrapper: FileWrapper) =>
    setFileWrappers(fileWrappers.filter(f => f.id !== fileWrapper.id));

  useEffect(() => {
    if (onChange) {
      onChange(fileWrappers.map(f => f.file));
    }
  }, [fileWrappers]);

  return (
    <StyledImageGallery>
      <StyledFileInput multiple accept={accept} onChange={onImageChange}>
        <CameraAltIcon fontSize="large" />
        <div>Attach File(s)</div>
      </StyledFileInput>
      {fileWrappers
        .filter(x => Boolean(x))
        .map(fileWrapper => {
          const isPDFBlob = fileWrapper.file.type === "application/pdf";
          return (
            <ImageSlide
              height={CUSTOM_IMAGE_HEIGHT}
              isPDF={isPDFBlob}
              key={fileWrapper.id}
            >
              <Image isPDF={isPDFBlob} src={fileWrapper.url} />
              <StyledCancelButton
                title="Delete Image"
                onClick={() => onImageDelete(fileWrapper)}
              >
                <HighlightOffIcon />
              </StyledCancelButton>
            </ImageSlide>
          );
        })}
    </StyledImageGallery>
  );
};
